import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/lib/supabase';
import { Database, ArrowRight, Table, GitBranch, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';

interface TableStats {
  tableName: string;
  rowCount: number;
  size: string;
  companySlug?: string;
}

interface CompanyInfo {
  id: string;
  name: string;
  slug: string;
  isActive: boolean;
}

const DataIngestionVisualizationPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState<CompanyInfo[]>([]);
  const [tableStats, setTableStats] = useState<TableStats[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch companies with slugs
      const { data: companiesData, error: companiesError } = await supabase
        .from('sw_companies')
        .select('id, name, slug, is_active')
        .not('slug', 'is', null)
        .order('name');

      if (companiesError) throw companiesError;

      setCompanies(companiesData?.map(c => ({
        id: c.id,
        name: c.name || 'Unknown',
        slug: c.slug || '',
        isActive: c.is_active ?? true
      })) || []);

      // Fetch table statistics
      const { data: statsData, error: statsError } = await supabase
        .rpc('get_table_statistics');

      if (statsError) {
        console.error('Stats error:', statsError);
        // Continue without stats
      } else {
        setTableStats(statsData || []);
      }

    } catch (err: any) {
      console.error('Error loading data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getTableCount = (companySlug: string, entity: string): number => {
    const tableName = `${companySlug}_${entity}`;
    const stat = tableStats.find(s => s.tableName === tableName);
    return stat?.rowCount || 0;
  };

  const entities = [
    { name: 'Customers', key: 'customers', icon: '👥', color: 'bg-blue-100 text-blue-800' },
    { name: 'Jobs', key: 'jobs', icon: '🔧', color: 'bg-green-100 text-green-800' },
    { name: 'Agreements', key: 'agreements', icon: '📄', color: 'bg-purple-100 text-purple-800' },
    { name: 'Assignments', key: 'assignments', icon: '📋', color: 'bg-orange-100 text-orange-800' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">SuccessWare Data Ingestion Architecture</h1>
        <p className="text-gray-600">Multi-tenant data isolation with per-company tables</p>
      </div>

      {error && (
        <Alert className="mb-6 bg-red-50 border-red-200">
          <AlertCircle className="h-4 w-4 text-red-600" />
          <AlertDescription className="text-red-800">{error}</AlertDescription>
        </Alert>
      )}

      {/* Architecture Overview */}
      <Card className="mb-8 border-2 border-blue-200 shadow-lg">
        <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50">
          <CardTitle className="flex items-center gap-2">
            <GitBranch className="h-6 w-6 text-blue-600" />
            Data Flow Architecture
          </CardTitle>
          <CardDescription>How SuccessWare data is ingested into isolated per-company tables</CardDescription>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            {/* Step 1: Source */}
            <div className="flex flex-col items-center flex-1">
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center shadow-lg">
                <Database className="h-12 w-12 text-white" />
              </div>
              <h3 className="mt-3 font-semibold text-gray-900">SuccessWare GraphQL API</h3>
              <p className="text-sm text-gray-600 text-center mt-1">Source system with customer, job, agreement data</p>
            </div>

            <ArrowRight className="h-8 w-8 text-gray-400 flex-shrink-0 mx-4" />

            {/* Step 2: Edge Functions */}
            <div className="flex flex-col items-center flex-1">
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center shadow-lg">
                <svg className="h-12 w-12 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2L2 7v10c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-10-5z"/>
                </svg>
              </div>
              <h3 className="mt-3 font-semibold text-gray-900">Edge Functions</h3>
              <p className="text-sm text-gray-600 text-center mt-1">Fetch, validate, deduplicate</p>
              <div className="flex gap-1 mt-2">
                <Badge variant="secondary" className="text-xs">successware-import</Badge>
                <Badge variant="secondary" className="text-xs">true-up</Badge>
              </div>
            </div>

            <ArrowRight className="h-8 w-8 text-gray-400 flex-shrink-0 mx-4" />

            {/* Step 3: Per-Company Tables */}
            <div className="flex flex-col items-center flex-1">
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center shadow-lg">
                <Table className="h-12 w-12 text-white" />
              </div>
              <h3 className="mt-3 font-semibold text-gray-900">Per-Company Tables</h3>
              <p className="text-sm text-gray-600 text-center mt-1">Isolated data storage by company slug</p>
              <div className="flex gap-1 mt-2">
                <Badge variant="secondary" className="text-xs">company_customers</Badge>
                <Badge variant="secondary" className="text-xs">company_jobs</Badge>
              </div>
            </div>

            <ArrowRight className="h-8 w-8 text-gray-400 flex-shrink-0 mx-4" />

            {/* Step 4: UNION Views */}
            <div className="flex flex-col items-center flex-1">
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center shadow-lg">
                <svg className="h-12 w-12 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z"/>
                </svg>
              </div>
              <h3 className="mt-3 font-semibold text-gray-900">Global Views</h3>
              <p className="text-sm text-gray-600 text-center mt-1">UNION ALL views for cross-company queries</p>
              <div className="flex gap-1 mt-2">
                <Badge variant="secondary" className="text-xs">customers_all_view</Badge>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Per-Company Data Status */}
      <div className="grid grid-cols-1 gap-6 mb-8">
        {companies.map((company) => (
          <Card key={company.id} className="border-2 shadow-md hover:shadow-lg transition-shadow">
            <CardHeader className="bg-gradient-to-r from-gray-50 to-slate-50">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    {company.name}
                    {company.isActive ? (
                      <Badge className="bg-green-100 text-green-800 border-green-300">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Active
                      </Badge>
                    ) : (
                      <Badge variant="secondary">Inactive</Badge>
                    )}
                  </CardTitle>
                  <CardDescription className="mt-1">
                    Slug: <code className="px-2 py-1 bg-gray-100 rounded text-xs font-mono">{company.slug}</code>
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="grid grid-cols-4 gap-4">
                {entities.map((entity) => {
                  const count = getTableCount(company.slug, entity.key);
                  return (
                    <div key={entity.key} className="flex flex-col items-center p-4 bg-white border-2 border-gray-200 rounded-lg hover:border-blue-300 transition-colors">
                      <div className={`w-16 h-16 rounded-full ${entity.color} flex items-center justify-center text-2xl mb-3 shadow-sm`}>
                        {entity.icon}
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-1">{entity.name}</h4>
                      <div className="text-3xl font-bold text-blue-600 mb-1">
                        {count.toLocaleString()}
                      </div>
                      <p className="text-xs text-gray-500 font-mono">
                        {company.slug}_{entity.key}
                      </p>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Key Features */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="border-2 border-blue-200">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              Data Isolation
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600">
              Each company's data is stored in separate tables (e.g., <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">hales_customers</code>), ensuring complete data isolation and security.
            </p>
          </CardContent>
        </Card>

        <Card className="border-2 border-green-200">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              Deduplication
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600">
              Unique constraints on <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">sw_service_account_id</code> prevent duplicate imports. Existing records are skipped automatically.
            </p>
          </CardContent>
        </Card>

        <Card className="border-2 border-purple-200">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              Global Queries
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600">
              UNION ALL views combine all company tables for cross-company analytics while maintaining underlying isolation.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Technical Details */}
      <Card className="mt-8 border-2 border-gray-300">
        <CardHeader>
          <CardTitle>Technical Implementation</CardTitle>
          <CardDescription>Database schema and import process details</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <h4 className="font-semibold mb-2">Table Naming Convention:</h4>
              <code className="block bg-gray-100 p-3 rounded text-sm font-mono">
                {'{'}company_slug{'}'}_{'{'} entity{'}'}
                <br />
                Examples: hales_customers, climate_jobs, abraham_agreements
              </code>
            </div>

            <div>
              <h4 className="font-semibold mb-2">Import Edge Functions:</h4>
              <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
                <li><code className="bg-gray-100 px-1 py-0.5 rounded">successware-import</code> - Initial bulk import of customers</li>
                <li><code className="bg-gray-100 px-1 py-0.5 rounded">successware-true-up</code> - Incremental sync to catch missing records</li>
                <li><code className="bg-gray-100 px-1 py-0.5 rounded">successware-import-jobs</code> - Job/work order imports</li>
                <li><code className="bg-gray-100 px-1 py-0.5 rounded">successware-import-agreements</code> - Service agreement imports</li>
                <li><code className="bg-gray-100 px-1 py-0.5 rounded">successware-import-assignments</code> - Technician assignment imports</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-2">Global Views:</h4>
              <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
                <li><code className="bg-gray-100 px-1 py-0.5 rounded">customers_all_view</code> - Combines all *_customers tables</li>
                <li><code className="bg-gray-100 px-1 py-0.5 rounded">sw_jobs_all_view</code> - Combines all *_jobs tables</li>
                <li><code className="bg-gray-100 px-1 py-0.5 rounded">sw_agreements_all_view</code> - Combines all *_agreements tables</li>
                <li><code className="bg-gray-100 px-1 py-0.5 rounded">sw_assignments_all_view</code> - Combines all *_assignments tables</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DataIngestionVisualizationPage;
